/**
 * Welcome — the five questions that make the app fit, asked once.
 *
 * Why this page exists: the profile audit (docs/PROFILE_UX_PASS_2026-09-06.md)
 * found ~50 settings of which a handful change behaviour, and NOT ONE profile
 * on prod had ever stored a scheduling preference. So every engine ran on
 * hardcoded defaults — 09:00 for everything — and the person was never asked.
 * A settings page nobody visits cannot fix that. Being asked once, at the
 * start, at the moment the answer starts paying off, can.
 *
 * The rules are pure and probed (`src/utils/onboarding.ts`,
 * `npm run test:onboarding`). Three things hold this page together:
 *   - one question per screen, so nothing competes for attention
 *   - every question says what the answer will change — no question is asked
 *     unless a running service reads the answer
 *   - nothing is required; Skip is a first-class button, not a grey escape
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { BRAND } from '../config/brand'
import { useBranding } from '../hooks/useBranding'
import {
  STEPS, DAY_KEYS, DAY_LABELS, DOOR_OPTIONS, initialAnswers, detectTimezone,
  dayShapeNote, dayShapeSummary, loadSummary, schedulingPayload,
  personalPayload, answeredSteps, doorPayload, opensTheFlow,
  closingLines, closingBlurb, closingTitle, whatHappensNext, deliberateSteps,
  type Answers, type DayKey, type DoorChoice,
} from '../utils/onboarding'

const TIMEZONES = [
  'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
  'America/Toronto', 'America/Sao_Paulo', 'Europe/London', 'Europe/Paris',
  'Europe/Berlin', 'Africa/Lagos', 'Africa/Johannesburg', 'Asia/Dubai',
  'Asia/Kolkata', 'Asia/Singapore', 'Asia/Shanghai', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland', 'UTC',
]

const zoneLabel = (tz: string) => tz.replace(/_/g, ' ').replace('/', ' · ')

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-app-micro font-medium text-secondary">{label}</span>
      {children}
    </label>
  )
}

const inputCls =
  'w-full rounded-lg border border-soft bg-surface px-3 py-2.5 text-app-body text-primary ' +
  'focus:border-brand focus:outline-none'

export function Welcome() {
  const navigate = useNavigate()
  const branding = useBranding()
  const start = useMemo(() => initialAnswers(detectTimezone()), [])
  const [a, setA] = useState<Answers>(start)
  const [i, setI] = useState(0)
  /** The door is shown first and only once; `null` means we are past it. */
  const [atDoor, setAtDoor] = useState(true)
  /** Set once the writes have landed; the flow ends on a panel, not a jump. */
  const [done, setDone] = useState(false)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const step = STEPS[i]
  const last = i === STEPS.length - 1
  const set = <K extends keyof Answers>(k: K, v: Answers[K]) => setA(p => ({ ...p, [k]: v }))

  const toggleDay = (d: DayKey) =>
    setA(p => ({
      ...p,
      workDays: p.workDays.includes(d) ? p.workDays.filter(x => x !== d) : [...p.workDays, d],
    }))

  /**
   * Save what was given and go. Each write is independent: a failure to create
   * the first dream must not cost the person their timezone, which is the
   * single most-read field in the app.
   */
  /**
   * Answer the door. "Not now" and "Don't ask again" both land on Today; only
   * the second one is remembered, because "not now" means exactly that and
   * first-run will offer again next session on its own.
   */
  async function answerDoor(choice: DoorChoice) {
    if (opensTheFlow(choice)) { setAtDoor(false); return }
    const payload = doorPayload(choice)
    if (payload) {
      setSaving(true)
      try {
        await fetch('/api/scheduling-preferences', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
      } catch {
        // Their answer was "leave me alone"; failing to record that must not
        // trap them here. Worst case they are asked once more.
      } finally {
        setSaving(false)
      }
    }
    navigate('/today')
  }

  async function finish() {
    setSaving(true)
    setFailed(null)
    const post = (url: string, body: unknown, method = 'PUT') =>
      fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

    const results = await Promise.allSettled([
      post('/api/profile/personal-info', personalPayload(a)),
      // `welcome_pending: false` retires an explicit re-offer; without it a
      // person re-invited to setup would be re-invited again next session.
      post('/api/scheduling-preferences', { ...schedulingPayload(a), welcome_pending: false }),
      a.dream.trim()
        ? post('/api/hierarchy/dreams', {
            title: a.dream.trim(), status: 'active', priority: 'high',
          }, 'POST')
        : Promise.resolve(null),
    ])

    // The first thing they named is the one worth mentioning if it did not land.
    if (a.dream.trim() && results[2].status === 'rejected') {
      setFailed('Your settings are saved. I could not save that first thing — you can add it from Dreams.')
      setSaving(false)
      return
    }
    setSaving(false)
    setDone(true)
  }

  const settled = answeredSteps(a, start)
  const note = step.id === 'day' ? dayShapeNote(a) : null

  if (done) {
    const lines = closingLines(a, start)
    // What they actually told us, which is not the same as what is now set:
    // a detected timezone is worth reading back but is nobody's answer.
    const deliberate = deliberateSteps(a, start).length
    return (
      <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center p-6">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-subtle">
            <Check className="h-5 w-5 text-brand" />
          </span>
          <div className="text-app-micro text-muted">{BRAND.name}</div>
        </div>

        <h1 className="text-2xl font-semibold text-primary">
          {closingTitle(deliberate)}
        </h1>
        <p className="mt-3 text-app-body leading-relaxed text-secondary">
          {closingBlurb(deliberate)}
        </p>

        {lines.length > 0 && (
          <ul className="mt-6 space-y-2" data-testid="closing-summary">
            {lines.map((l) => (
              <li key={l} className="flex items-start gap-2.5 text-app-body text-primary">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                <span>{l}</span>
              </li>
            ))}
          </ul>
        )}

        {/* What happens next, so the app is not a blank room after this. */}
        <div className="mt-7 rounded-xl border border-soft bg-surface-alt px-4 py-3.5">
          <p className="text-app-body text-secondary">
            {whatHappensNext(a, start, BRAND.assistant)}
          </p>
        </div>

        <div className="mt-8">
          <Button onClick={() => navigate('/today')} data-testid="closing-go">
            Take me to today <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    )
  }

  if (atDoor) {
    return (
      <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center p-6">
        <div className="mb-7 flex items-center gap-3">
          <img src={branding.icon} alt="" className="h-10 w-10" />
          <div className="text-app-micro text-muted">{BRAND.name}</div>
        </div>

        <h1 className="text-2xl font-semibold text-primary">
          Hello — I&rsquo;m {BRAND.assistant}.
        </h1>
        <p className="mt-3 text-app-body leading-relaxed text-secondary">
          I help you hold on to what matters and turn it into something you can
          actually do this week.
        </p>
        <p className="mt-3 text-app-body leading-relaxed text-secondary">
          It works far better if I know a little about your days — when you are
          awake, when you work, how much is a lot for you. Otherwise I will
          guess, and my guess is nine in the morning for everything.
        </p>

        <div className="mt-8 space-y-2">
          {DOOR_OPTIONS.map((o, n) => (
            <button
              key={o.choice}
              type="button"
              disabled={saving}
              onClick={() => void answerDoor(o.choice)}
              data-testid={`door-${o.choice}`}
              className={`flex w-full items-center justify-between gap-4 rounded-xl border px-4 py-3.5 text-left transition-colors disabled:opacity-60 ${
                n === 0
                  ? 'border-brand bg-brand-subtle hover:bg-brand-subtle/70'
                  : 'border-soft hover:bg-surface-alt'
              }`}
            >
              <span>
                <span className={`block text-app-body font-semibold ${n === 0 ? 'text-brand' : 'text-primary'}`}>
                  {o.label}
                </span>
                <span className="mt-0.5 block text-app-micro text-muted">{o.detail}</span>
              </span>
              {n === 0 && <ArrowRight className="h-4 w-4 shrink-0 text-brand" />}
            </button>
          ))}
        </div>

        <p className="mt-6 text-app-micro text-muted">
          Nothing here is required, and every answer can be changed later in Settings.
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center p-6">
      {/* Where you are, without a progress bar shouting at you. */}
      <div className="mb-8 flex items-center gap-3">
        <img src={branding.icon} alt="" className="h-8 w-8" />
        <div className="flex-1">
          <div className="text-app-micro text-muted">
            {/* The product name is a nicety; the count is the useful half, so
                the name is what goes when the line would wrap on a phone. */}
            <span className="hidden sm:inline">Setting up {BRAND.name} · </span>
            {i + 1} of {STEPS.length}
          </div>
        </div>
        <div className="flex gap-1" aria-hidden>
          {STEPS.map((s, n) => (
            <span
              key={s.id}
              className={`h-1 w-6 rounded-full ${n <= i ? 'bg-brand' : 'bg-soft'}`}
            />
          ))}
        </div>
      </div>

      <h1 className="text-2xl font-semibold text-primary">{step.question}</h1>
      <p className="mt-2 text-app-body text-secondary">{step.because}</p>

      <div className="mt-7 space-y-4">
        {step.id === 'name' && (
          <input
            autoFocus
            className={inputCls}
            value={a.name}
            onChange={e => set('name', e.target.value)}
            placeholder="Your name, or what you'd rather be called"
          />
        )}

        {step.id === 'place' && (
          <Field label="Your timezone">
            <select className={inputCls} value={a.timezone} onChange={e => set('timezone', e.target.value)}>
              {[...new Set([a.timezone, ...TIMEZONES])].map(tz => (
                <option key={tz} value={tz}>{zoneLabel(tz)}</option>
              ))}
            </select>
          </Field>
        )}

        {step.id === 'day' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="You wake at">
                <input type="time" className={inputCls} value={a.wake} onChange={e => set('wake', e.target.value)} />
              </Field>
              <Field label="You sleep at">
                <input type="time" className={inputCls} value={a.sleep} onChange={e => set('sleep', e.target.value)} />
              </Field>
              <Field label="Work starts">
                <input type="time" className={inputCls} value={a.workStart} onChange={e => set('workStart', e.target.value)} />
              </Field>
              <Field label="Work ends">
                <input type="time" className={inputCls} value={a.workEnd} onChange={e => set('workEnd', e.target.value)} />
              </Field>
            </div>
            <div>
              <span className="mb-1.5 block text-app-micro font-medium text-secondary">Days you work</span>
              <div className="flex flex-wrap gap-2">
                {DAY_KEYS.map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(d)}
                    aria-pressed={a.workDays.includes(d)}
                    className={`rounded-lg border px-3 py-1.5 text-app-micro transition-colors ${
                      a.workDays.includes(d)
                        ? 'border-brand bg-brand-subtle font-medium text-brand'
                        : 'border-soft text-secondary hover:bg-surface-alt'
                    }`}
                  >
                    {DAY_LABELS[d]}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-app-micro text-muted">{dayShapeSummary(a)}</p>
            {note && <p className="text-app-micro text-warn">{note}</p>}
          </>
        )}

        {step.id === 'load' && (
          <>
            <Field label="Things a day">
              <select className={inputCls} value={a.perDay} onChange={e => set('perDay', Number(e.target.value))}>
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>{n === 1 ? 'One thing' : `${n} things`}</option>
                ))}
              </select>
            </Field>
            <Field label="How long you can concentrate in one go">
              <select className={inputCls} value={a.focusBlock} onChange={e => set('focusBlock', Number(e.target.value))}>
                <option value={25}>25 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>An hour</option>
                <option value={90}>90 minutes</option>
              </select>
            </Field>
            <p className="text-app-micro text-muted">{loadSummary(a)}</p>
          </>
        )}

        {step.id === 'first' && (
          <>
            <input
              autoFocus
              className={inputCls}
              value={a.dream}
              onChange={e => set('dream', e.target.value)}
              placeholder="e.g. I can run a half marathon"
            />
            <p className="text-app-micro text-muted">
              It does not have to be right. You can rename it, or let it go, whenever you like.
            </p>
          </>
        )}
      </div>

      {failed && <p className="mt-5 text-app-micro text-warn">{failed}</p>}

      <div className="mt-9 flex items-center gap-2">
        {/* From the first question, Back returns to the door rather than
            dead-ending — the way out has to stay reachable at every step. */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => (i === 0 ? setAtDoor(true) : setI(i - 1))}
          disabled={saving}
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={() => (last ? finish() : setI(i + 1))} disabled={saving}>
          Skip
        </Button>
        <Button size="sm" onClick={() => (last ? finish() : setI(i + 1))} disabled={saving}>
          {saving ? (
            <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Saving…</>
          ) : last ? (
            <><Check className="mr-1 h-4 w-4" /> Start</>
          ) : (
            <>Next <ArrowRight className="ml-1 h-4 w-4" /></>
          )}
        </Button>
      </div>

      <p className="mt-6 text-app-micro text-muted">
        {settled.length === 0
          ? 'Nothing here is required — you can change any of it later in Settings.'
          : `Set up so far: ${settled.length} of ${STEPS.length}. All of it is editable later in Settings.`}
      </p>
    </div>
  )
}

export default Welcome
